import { PropsWithChildren, useEffect, useState } from 'react';
import { useRouter } from '@uirouter/react';
import { Trans, useTranslation } from 'react-i18next';

import { Widget, WidgetBody } from '@/core/components/widget';
import { FormControl } from '@/core/components/form-components/FormControl';
import { Button } from '@/core/components/Button';
import { FormSectionTitle } from '@/core/components/form-components/FormSectionTitle';
import { TextTip } from '@/core/components/Tip/TextTip';
import { Code } from '@/core/components/Code';
import { CopyButton } from '@/core/components/Button/CopyButton';
import { Input } from '@/core/components/form-components/Input';

import styles from './CreateAccessToken.module.css';

interface AccessTokenResponse {
  rawAPIKey: string;
}

export interface Props {
  // Идентификатор пользователя, для которого создается токен
  userId: number;

  // Создает токен на сервере; результат содержит его открытое значение
  onSubmit: (description: string) => Promise<AccessTokenResponse>;

  // Вызывается при ошибке (обертка над Notifications.error)
  onError: (heading: string, err: unknown, message: string) => void;
}

export function CreateAccessToken({
  onSubmit,
  onError,
}: PropsWithChildren<Props>) {
  const { t } = useTranslation();

  const router = useRouter();
  const [description, setDescription] = useState('');
  const [errorText, setErrorText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [accessToken, setAccessToken] = useState('');

  useEffect(() => {
    if (description.length === 0) {
      setErrorText(
        t(
          'users.access-tokens.create.form.description-field.error.required',
          'Обязательное поле'
        )
      );
    } else setErrorText('');
  }, [description, t]);

  async function generateAccessToken() {
    if (isLoading) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await onSubmit(description);
      setAccessToken(response.rawAPIKey);
    } catch (err) {
      onError('Ошибка', err, 'Не удалось создать токен доступа');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Widget>
      <WidgetBody>
        <div>
          <FormControl
            inputId="input"
            label={t(
              'users.access-tokens.create.form.description-field.label',
              'Описание'
            )}
            errors={errorText}
          >
            <Input
              id="input"
              onChange={(e) => setDescription(e.target.value)}
              value={description}
            />
          </FormControl>
          <Button
            disabled={!!errorText || !!accessToken}
            onClick={() => generateAccessToken()}
            className={styles.addButton}
          >
            {t(
              'users.access-tokens.create.add-button',
              'Добавить токен доступа'
            )}
          </Button>
        </div>
        {accessToken && (
          <>
            <FormSectionTitle>
              <Trans i18nKey="users.access-tokens.create.new-access-token.title">
                Новый токен доступа
              </Trans>
            </FormSectionTitle>
            <TextTip>
              <Trans i18nKey="users.access-tokens.create.new-access-token.explanation">
                Скопируйте и сохраните токен: после закрытия страницы он больше
                не будет показан.
              </Trans>
            </TextTip>
            <Code>{accessToken}</Code>
            <CopyButton copyText={accessToken} className={styles.copyButton}>
              <Trans i18nKey="users.access-tokens.create.new-access-token.copy-button">
                Копировать токен доступа
              </Trans>
            </CopyButton>
            <hr />
            <Button
              type="button"
              onClick={() => router.stateService.go('shbdeployer.account')}
            >
              <Trans i18nKey="users.access-tokens.create.done-button">
                Готово
              </Trans>
            </Button>
          </>
        )}
      </WidgetBody>
    </Widget>
  );
}
